import {FC, useState, useEffect, useCallback} from 'react'
import {getLoginAttempts, LoginAttemptRow, LoginAttemptsResponse} from './_requests'

const METHOD_LABELS: Record<string, string> = {
  password: 'אימייל/סיסמה',
  phone_send: 'שליחת קוד SMS',
  phone_verify: 'אימות קוד SMS',
  facebook: 'פייסבוק',
  google: 'גוגל',
}

const REASON_LABELS: Record<string, string> = {
  ok: 'הצליח',
  wrong_password: 'סיסמה שגויה',
  user_not_found: 'משתמש לא נמצא',
  no_active_account: 'אין חשבון פעיל',
  inactive_account: 'חשבון לא פעיל (פג תוקף)',
  no_account: 'מספר לא רשום',
  rate_limited: 'חסום (25 דק׳)',
  ip_rate_limited: 'חסום (IP)',
  invalid_phone: 'טלפון לא תקין',
  code_expired: 'קוד פג תוקף',
  code_mismatch: 'קוד שגוי',
  too_many_attempts: 'יותר מדי ניסיונות',
  not_configured: 'לא מוגדר',
  sms_send_failed: 'שליחת SMS נכשלה',
  invalid_token: 'טוקן לא תקין',
  no_email: 'אין אימייל',
  server_error: 'שגיאת שרת',
}

const methodBadge = (m: string) => {
  const colors: Record<string, string> = {
    password: 'badge-light-primary',
    phone_send: 'badge-light-info',
    phone_verify: 'badge-light-success',
    facebook: 'badge-light-primary',
    google: 'badge-light-warning',
  }
  return colors[m] || 'badge-light'
}

const accountName = (a: LoginAttemptRow['account']) => {
  if (!a) return '—'
  const name = [a.first_name, a.last_name].filter(Boolean).join(' ').trim()
  return name || a.email || a.username || a._id
}

const fmtDate = (s: string) => {
  try {
    return new Date(s).toLocaleString('he-IL', {dateStyle: 'short', timeStyle: 'medium'})
  } catch {
    return s
  }
}

const LoginAttemptsPage: FC = () => {
  const [data, setData] = useState<LoginAttemptsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [method, setMethod] = useState('')
  const [success, setSuccess] = useState('')
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [days, setDays] = useState(7)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getLoginAttempts({
        page,
        limit: 50,
        method: method || undefined,
        success: success || undefined,
        search: search || undefined,
        days,
      })
      setData(res.data)
    } catch (e) {
      console.error('Failed to load login attempts:', e)
    }
    setLoading(false)
  }, [page, method, success, search, days])

  useEffect(() => { load() }, [load])

  // Reset to page 1 whenever a filter changes.
  useEffect(() => { setPage(1) }, [method, success, search, days])

  const s = data?.summary
  const successRate = s && s.total > 0 ? Math.round((s.success / s.total) * 100) : 0

  return (
    <div className='container-fluid py-6' dir='rtl'>
      <div className='d-flex justify-content-between align-items-center mb-6'>
        <h1 className='fw-bolder m-0'>ניסיונות התחברות</h1>
        <select className='form-select form-select-sm w-auto' value={days} onChange={(e) => setDays(Number(e.target.value))}>
          <option value={1}>24 שעות</option>
          <option value={7}>7 ימים</option>
          <option value={30}>30 ימים</option>
          <option value={90}>90 ימים</option>
        </select>
      </div>

      {/* Summary cards */}
      {s && (
        <div className='row g-4 mb-6'>
          <div className='col-6 col-md-3'>
            <div className='card card-flush h-100'>
              <div className='card-body py-4'>
                <div className='text-gray-500 fs-7'>סה״כ ניסיונות</div>
                <div className='fs-2 fw-bolder'>{s.total.toLocaleString()}</div>
              </div>
            </div>
          </div>
          <div className='col-6 col-md-3'>
            <div className='card card-flush h-100'>
              <div className='card-body py-4'>
                <div className='text-gray-500 fs-7'>הצליחו</div>
                <div className='fs-2 fw-bolder text-success'>{s.success.toLocaleString()}</div>
              </div>
            </div>
          </div>
          <div className='col-6 col-md-3'>
            <div className='card card-flush h-100'>
              <div className='card-body py-4'>
                <div className='text-gray-500 fs-7'>נכשלו</div>
                <div className='fs-2 fw-bolder text-danger'>{s.failed.toLocaleString()}</div>
              </div>
            </div>
          </div>
          <div className='col-6 col-md-3'>
            <div className='card card-flush h-100'>
              <div className='card-body py-4'>
                <div className='text-gray-500 fs-7'>אחוז הצלחה</div>
                <div className='fs-2 fw-bolder'>{successRate}%</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Per-method breakdown */}
      {s && Object.keys(s.by_method).length > 0 && (
        <div className='card card-flush mb-6'>
          <div className='card-body py-4'>
            <div className='d-flex flex-wrap gap-4'>
              {Object.entries(s.by_method).map(([m, v]) => (
                <div key={m} className='d-flex align-items-center gap-2'>
                  <span className={`badge ${methodBadge(m)}`}>{METHOD_LABELS[m] || m}</span>
                  <span className='fw-bold'>{v.total}</span>
                  <span className='text-success fs-8'>✓{v.success}</span>
                  <span className='text-danger fs-8'>✗{v.failed}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className='card card-flush mb-4'>
        <div className='card-body py-4'>
          <div className='row g-3 align-items-end'>
            <div className='col-md-3'>
              <label className='form-label fs-8 text-gray-500'>שיטה</label>
              <select className='form-select form-select-sm' value={method} onChange={(e) => setMethod(e.target.value)}>
                <option value=''>הכל</option>
                {Object.entries(METHOD_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div className='col-md-3'>
              <label className='form-label fs-8 text-gray-500'>תוצאה</label>
              <select className='form-select form-select-sm' value={success} onChange={(e) => setSuccess(e.target.value)}>
                <option value=''>הכל</option>
                <option value='true'>הצליח</option>
                <option value='false'>נכשל</option>
              </select>
            </div>
            <div className='col-md-4'>
              <label className='form-label fs-8 text-gray-500'>חיפוש (טלפון / אימייל / IP)</label>
              <input
                className='form-control form-control-sm'
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') setSearch(searchInput) }}
                placeholder='חפש...'
              />
            </div>
            <div className='col-md-2'>
              <button className='btn btn-sm btn-primary w-100' onClick={() => setSearch(searchInput)}>חפש</button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className='card card-flush'>
        <div className='card-body py-4'>
          {loading ? (
            <div className='text-center py-10'>
              <span className='spinner-border text-primary' role='status'></span>
            </div>
          ) : !data || data.attempts.length === 0 ? (
            <div className='text-center text-gray-500 py-10'>לא נמצאו ניסיונות התחברות</div>
          ) : (
            <div className='table-responsive'>
              <table className='table table-row-bordered table-row-gray-200 align-middle gs-0 gy-3'>
                <thead>
                  <tr className='fw-bold text-gray-600 fs-7'>
                    <th>מתי</th>
                    <th>שיטה</th>
                    <th>תוצאה</th>
                    <th>מזהה</th>
                    <th>חשבון</th>
                    <th>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {data.attempts.map((a) => (
                    <tr key={a._id}>
                      <td className='text-nowrap fs-7'>{fmtDate(a.created_at)}</td>
                      <td><span className={`badge ${methodBadge(a.method)}`}>{METHOD_LABELS[a.method] || a.method}</span></td>
                      <td>
                        {a.success ? (
                          <span className='badge badge-light-success'>הצליח</span>
                        ) : (
                          <span className='badge badge-light-danger'>{REASON_LABELS[a.reason] || a.reason || 'נכשל'}</span>
                        )}
                      </td>
                      <td className='fs-7' dir='ltr' style={{textAlign: 'right'}}>{a.identifier || '—'}</td>
                      <td className='fs-7'>{accountName(a.account)}</td>
                      <td className='fs-8 text-gray-500' dir='ltr' style={{textAlign: 'right'}}>{a.ip || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {data && data.pagination.pages > 1 && (
            <div className='d-flex justify-content-between align-items-center mt-4'>
              <div className='text-gray-500 fs-7'>
                עמוד {data.pagination.page} מתוך {data.pagination.pages} ({data.pagination.total.toLocaleString()} רשומות)
              </div>
              <div className='d-flex gap-2'>
                <button className='btn btn-sm btn-light' disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>הקודם</button>
                <button className='btn btn-sm btn-light' disabled={page >= data.pagination.pages} onClick={() => setPage((p) => p + 1)}>הבא</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default LoginAttemptsPage
